import { CacheUtil } from "@base/util/Cache";
import { PartialRecord } from "@base/util/TypeUtil";
import { Product } from "@biz/definitions/Product";
import { IPoleData, ISunSailData, PointName, PoleId, PoleIndex, PolePositionType, SunSailType } from "@biz/definitions/SunSailDefinitions";
import { getLetterForNumber, getPointName } from "@biz/definitions/SunSailDefinitions.util";
import { ISail2dForm } from "@biz/models/Sail2dDefinitions";
import { PD } from "@biz/PD";
import { ISCPole, ISCSunSailData } from "@biz/sc/sunSail/ISCSunSailData";

export class Poles {
  static isCornerPole(poleId: PoleId): boolean {
    return poleId < Poles.CENTER_POLES_START;
  }
  static isCenterPole(poleId: PoleId): boolean {
    return poleId >= Poles.CENTER_POLES_START;
  }

  static getCount(sunSail: ISunSailData | ISail2dForm | ISCSunSailData, positionType: PolePositionType = "corner"): number {
    if ("pd" in sunSail && sunSail.pd.pdt === "sunSail")
      return { corner: sunSail.pd.poleCount, center: 0 }[positionType];
    else if ("poleCount" in sunSail) {
      const center = "centerPoles" in sunSail ? sunSail.centerPoles?.length || 0 : 0;
      return { corner: sunSail.poleCount, center }[positionType];
    } else {
      return { corner: sunSail.poles.length, center: sunSail.centerPoles?.length ?? 0 }[positionType];
    }
    throw new Error("Invalid sunSail!");
  }

  static isValid(sunSail: ISunSailData | ISail2dForm, poleId: PoleId): boolean {
    const pid = Poles.fromPoleId(poleId);
    return pid.poleIndex >= 0 && pid.poleIndex < Poles.getCount(sunSail, pid.polePositionType);
  }

  static getCountObject(sunSail: ISunSailData | ISail2dForm | ISCSunSailData): { corner: number, center: number } {
    if ("pd" in sunSail && sunSail.pd.pdt === "sunSail")
      return { corner: sunSail.pd.poleCount, center: 0 };
    if ("poleCount" in sunSail)
      return { corner: sunSail.poleCount, center: 0 };
    else if ("poles" in sunSail)
      return { corner: Poles.getCount(sunSail), center: Poles.getCount(sunSail, "center") };
    throw new Error("Invalid sunSail!");
  }

  static EMPTY_POLES: ISunSailData["poles"] = [];
  static getPoles<T extends ISunSailData | ISCSunSailData>(sunSail: T, type: PolePositionType): T["poles"] {
    if (type === "corner") {
      return sunSail.poles;
    } else if (type === "center") {
      if (Product.isProduct3d(sunSail))
        return sunSail.centerPoles || Poles.EMPTY_POLES;
    }
    return Poles.EMPTY_POLES;
  }

  static CENTER_POLES_START = 1000;
  static getPole<T extends ISunSailData | ISCSunSailData | ISail2dForm>(sunSail: T, poleId: PoleId, options?: { offset?: number }): T["poles"][0] {
    const pid = Poles.fromPoleId(poleId)
    const poleIndex = options?.offset ? Poles.calcPoleIndexWithOffset(pid.poleIndex, Poles.getCount(sunSail, pid.polePositionType), options.offset) : pid.poleIndex;

    const pole = pid.polePositionType === "center" ? sunSail.centerPoles![poleIndex] : sunSail.poles?.[poleIndex];
    if (!pole)
      throw new Error(`Invalid poleId: ${poleId}`);
    return pole;
  }

  static calcPoleIndexWithOffset(poleIndex: PoleIndex, count: number, offset: number): PoleIndex {
    return (poleIndex + offset + count) % count;
  }

  static calcPoleIdWithOffset(poleId: PoleId, offset: number, sunSail: ISunSailData): PoleId {
    const pid = Poles.fromPoleId(poleId);
    return Poles.toPoleId(Poles.calcPoleIndexWithOffset(pid.poleIndex, Poles.getCount(sunSail, pid.polePositionType), offset), pid.polePositionType);
  }

  static getPolePositionType(poleId: PoleId): PolePositionType {
    return poleId >= Poles.CENTER_POLES_START ? "center" : "corner";
  }

  static getPolePositionProperty(polePositionType: PolePositionType): "centerPoles" | "poles" {
    return polePositionType === "corner" ? "poles" : "centerPoles"
  }

  static toPoleId(poleIndex: PoleIndex, polePositionType: PolePositionType): PoleId {
    if (poleIndex < 0)
      poleIndex = 0;
    if (polePositionType === "center")
      return poleIndex + Poles.CENTER_POLES_START
    return poleIndex;
  }

  static fromPoleId(poleId: PoleId): { poleIndex: PoleIndex, polePositionType: PolePositionType } {
    const polePositionType = Poles.getPolePositionType(poleId);
    if (polePositionType === "center")
      return { poleIndex: poleId - Poles.CENTER_POLES_START, polePositionType };
    return { poleIndex: poleId as PoleIndex, polePositionType };
  }

  static getPoleIds(ssd: ISunSailData | ISail2dForm): PoleId[] {
    return Poles.getPoleIdsFromCounts(Poles.getCount(ssd, "corner"), Poles.getCount(ssd, "center"));
  }

  static getPoleIdsFromCount(count: number, positionType: PolePositionType = "corner"): PoleId[] {
    const poleIds: PoleId[] = [];
    for (let i = 0; i < count; i++) {
      poleIds.push(Poles.toPoleId(i, positionType));
    }
    return poleIds;
  }

  static getPoleIdsFromCounts(cornerCount: number, centerCount: number): PoleId[] {
    const poleIds: PoleId[] = [
      ...Poles.getPoleIdsFromCount(cornerCount, "corner"),
      ...Poles.getPoleIdsFromCount(centerCount, "center"),
    ];
    return poleIds;
  }

  static #calcPoleNames(_id: string, poles: IPoleData[] | ISCPole[], centerPoleCount: number): Record<PoleId, string> {
    const ret: PartialRecord<PoleId, string> = {};

    let mainPoleIndex = 0;
    let subPoleIndex = 0;
    for (let i = 0; i < poles.length; i++) {
      const p = poles[i];
      if (p.additionalPole) {
        subPoleIndex++;
      } else {
        mainPoleIndex++;
        subPoleIndex = 0;
      }
      ret[i as PoleId] = getLetterForNumber(mainPoleIndex - 1, "A") as PointName + "'".repeat(subPoleIndex);
    }

    for (let i = 0; i < centerPoleCount; i++) {
      ret[Poles.toPoleId(i, "center")] = `📍${i + 1}` as PointName;
    }

    return ret as Record<PoleId, string>;
  }
  static _calcPoleNames = CacheUtil.memo(Poles.#calcPoleNames, "Poles.calcPoleNames", {
    idsFromArgs: 1,
    inputsEqualFn: "ref",
    resultEqualFn: "shallow",
  })

  static calcPoleNames(ssd: ISunSailData | ISail2dForm | ISCSunSailData): Record<PoleId, string> {
    return Poles._calcPoleNames(ssd.id, ssd.poles, ssd.centerPoles?.length || 0);
  }

  static getPointNameCached(ssd: ISunSailData | ISail2dForm | ISCSunSailData, poleId: PoleId): string {
    if (!ssd.poles)
      return Poles.getPointNameCalc(ssd, poleId);
    return Poles.calcPoleNames(ssd)[poleId];
  }

  static getPointNameCalc(ssd: ISunSailData | ISail2dForm | ISCSunSailData, poleId: PoleId): string {
    const pid = Poles.fromPoleId(poleId);
    if (pid.polePositionType === "center")
      return `📍${pid.poleIndex + 1}` as PointName;


    const sunSailType = PD.productToSunSailType(ssd);
    if (sunSailType === SunSailType.SandyRect) {
      let mainPoleIndex = 0;
      let subPoleIndex = 0;
      for (let i = 0; i < ssd.poles.length; i++) {
        const p = ssd.poles[i];
        if (p.additionalPole) {
          subPoleIndex++;
        } else {
          mainPoleIndex++;
          subPoleIndex = 0;
        }
        if (i === poleId) {
          return Poles.getSubPointName(mainPoleIndex, subPoleIndex)
        }
      }
    }

    return getPointName(poleId);
  }

  static getPointText(ssd: ISunSailData | ISail2dForm | ISCSunSailData, poleId: PoleId): string {
    return Poles.getPointNameCached(ssd, poleId);
  }

  static getSubPointName(mainPoleIndex: number, subPoleIndex: number): string {
    return getLetterForNumber(mainPoleIndex, "A") as PointName + "'".repeat(subPoleIndex);
  }

  static getSideText(ssd: ISunSailData | ISail2dForm | ISCSunSailData, aIndex: PoleId, bIndex: PoleId, sort: boolean = true): string {
    if (sort && aIndex > bIndex) {
      [aIndex, bIndex] = [bIndex, aIndex];
    }
    return (Poles.getPointText(ssd, aIndex) + Poles.getPointText(ssd, bIndex));
  }

  static getEdgeText(ssd: ISunSailData | ISail2dForm | ISCSunSailData, aIndex: PoleId, bIndex: PoleId, sort: boolean = true): string {
    if (sort && aIndex > bIndex) {
      [aIndex, bIndex] = [bIndex, aIndex];
    }

    return (Poles.getPointText(ssd, aIndex) + Poles.getPointText(ssd, bIndex)).toLowerCase();
  }

  static getOtherPole(sunSailType: SunSailType, poleId: PoleId): PoleIndex {
    switch (sunSailType) {
      case SunSailType.SolidayA:
      case SunSailType.SolidayOne:
      case SunSailType.SolidayFlex:
      case SunSailType.SolidayM:
      case SunSailType.SolidayC:
      case SunSailType.SolidayX: {
        switch (poleId) {
          case PoleIndex.A:
            return PoleIndex.C;
          case PoleIndex.B:
            return PoleIndex.D;
          case PoleIndex.C:
            return PoleIndex.A;
          case PoleIndex.D:
            return PoleIndex.B;
        }
        break;
      }
      case SunSailType.SolidayCS:
      case SunSailType.SolidayTwin:
      case SunSailType.SolidayXS: {
        switch (poleId) {
          case PoleIndex.A:
            return PoleIndex.B;
          case PoleIndex.B:
            return PoleIndex.A;
          case PoleIndex.C:
            return PoleIndex.D;
          case PoleIndex.D:
            return PoleIndex.C;
          case PoleIndex.E:
            return PoleIndex.F;
          case PoleIndex.F:
            return PoleIndex.E;
        }
      }
    }
    throw new Error("Not supported so far!");
  }
}